import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, styled, CircularProgress } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { UserAccount } from '../../../data/models/user-account.model';
import {
  UserCategoryType,
  UserCategoryTypeBC,
} from '../../../data/models/user-category-type.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  fetchUserAccountsAsync,
  selectUserAccounts,
  selectSavingUser,
  selectSavedUser,
  selectUserInfo,
  selectLoadingUserInfo,
  fetchUserInfoAsync,
  setUserInfo,
  selectLoadingUserAccounts,
  deleteUserAsync,
  fetchOnboardingUserAccountsAsync,
  selectOnboardingUserAccounts,
  sendPasswordResetAsync,
  sendAccountActivationAsync,
  selectDeletedUser,
} from '../../../data/user.slice';
import {
  fetchLookupItemsAsync,
  selectUserCategoryTypes,
} from '../../../data/lookup.slice';
import UserDialog from './UserDialog';
import UserDataTable from './UserDataTable';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useCanManageMolinaUsers } from '../../../hooks/permissions/useCanManageMolinaUsers';
import { useCanCreateMolinaUsers } from '../../../hooks/permissions/useCanCreateMolinaUsers';
import userService from '../../../services/userService';
import CommonToolbarWithSearchBar, {
  ExportEntity,
  ExportFilePrefix,
} from './CommonToolbarWithSearchBar';
import { firstValueFrom } from 'rxjs';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'Users';

export const classes = {
  container: `${PREFIX}-container`,
  toolbar: `${PREFIX}-toolbar`,
  search: `${PREFIX}-search`,
  loading: `${PREFIX}-loading`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },

  [`& .${classes.search}`]: {
    flexGrow: 1,
  },

  [`&.${classes.loading}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

enum ConfirmationDialogType {
  Delete = 'delete',
  ResetPassword = 'resetPassword',
  ActivationEmail = 'activationEmail',
}

type ConfirmationDialogState = {
  type: ConfirmationDialogType;
  title: string;
  message: string;
  onConfirm: () => void;
} | null;

export enum UserExportColumn {
  FirstName = 'First Name',
  LastName = 'Last Name',
  Email = 'Email',
  Active = 'Active',
  UserType = 'User Type',
  TransportationProvider = 'Transportation Provider',
  IdentityProvider = 'Identity Provider',
}

const mapUserToExport = (
  user: UserAccount,
  userCategoryTypes: UserCategoryType[],
) => {
  const userTypeObj = userCategoryTypes?.find(
    (type) => type.userCategoryTypeID === user.userCategoryTypeID,
  );
  return {
    [UserExportColumn.FirstName]: user.userFirstName ?? '',
    [UserExportColumn.LastName]: user.userLastName ?? '',
    [UserExportColumn.Email]: user.emailAddress ?? '',
    [UserExportColumn.Active]:
      user.userAccountState === 'active' ? 'True' : 'False',
    [UserExportColumn.UserType]: userTypeObj?.userCategoryTypeName ?? '',
    [UserExportColumn.TransportationProvider]:
      user.transportationProvider?.transportationProviderName ?? '',
    [UserExportColumn.IdentityProvider]: user.identityAccess ?? '',
  };
};

export function Users() {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const canManageMolinaUsers = useCanManageMolinaUsers();
  const canCreateMolinaUsers = useCanCreateMolinaUsers();
  const isSuperReaderUser = useIsSuperReaderUser();

  const user = useAppSelector(selectUserInfo);
  const loadingUserAccounts = useAppSelector(selectLoadingUserAccounts);
  const userAccounts = useAppSelector(selectUserAccounts);
  const onboardingUserAccounts = useAppSelector(selectOnboardingUserAccounts);
  const userCategoryTypes = useAppSelector(selectUserCategoryTypes);
  const savingUser = useAppSelector(selectSavingUser);
  const savedUser = useAppSelector(selectSavedUser);
  const loadingSelectedUser = useAppSelector(selectLoadingUserInfo);
  const deletedUser = useAppSelector(selectDeletedUser);

  // Separate sort models for each table
  const [userSortModel, setUserSortModelLocal] = useState<GridSortModel>([
    { field: 'userLastName', sort: 'asc' },
  ]);
  const [onboardingSortModel, setOnboardingSortModelLocal] =
    useState<GridSortModel>([{ field: 'userLastName', sort: 'asc' }]);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  // const [userType, setUserType] = useState('standard');

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [onboardingPage, setOnboardingPage] = useState(1);
  const [onboardingPageSize, setOnboardingPageSize] = useState(10);

  const [confirmationDialogState, setConfirmationDialogState] =
    useState<ConfirmationDialogState>(null);

  const showConfirmationDialog = (
    type: ConfirmationDialogType,
    title: string,
    message: string,
    onConfirm: () => void,
  ) => {
    setConfirmationDialogState({ type, title, message, onConfirm });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogState(null);
  };

  const userSortModelMemo = useMemo(() => {
    const sort = userSortModel[0];
    const direction: 'asc' | 'desc' =
      sort && ['asc', 'desc'].includes(sort.sort as string)
        ? (sort.sort as 'asc' | 'desc')
        : 'asc';
    return sort ? { column: sort.field, direction } : null;
  }, [userSortModel]);

  const onboardingSortModelMemo = useMemo(() => {
    const sort = onboardingSortModel[0];
    const direction: 'asc' | 'desc' =
      sort && ['asc', 'desc'].includes(sort.sort as string)
        ? (sort.sort as 'asc' | 'desc')
        : 'asc';
    return sort ? { column: sort.field, direction } : null;
  }, [onboardingSortModel]);

  const fetchUserAccounts = useCallback(() => {
    dispatch(
      fetchUserAccountsAsync(
        pageSize,
        page,
        searchText,
        userSortModelMemo,
        false,
      ),
    );
  }, [pageSize, page, searchText, userSortModelMemo]);

  const fetchOnboardingUserAccounts = useCallback(() => {
    dispatch(
      fetchOnboardingUserAccountsAsync(
        onboardingPageSize,
        onboardingPage,
        searchText,
        onboardingSortModelMemo,
        false,
      ),
    );
  }, [onboardingPageSize, onboardingPage, searchText, onboardingSortModelMemo]);

  useEffect(() => {
    const curPartnerName = userService.getPartnerName();
    const newPartnerName = canManageMolinaUsers ? 'molina' : 'standard';
    const sort = userSortModel[0]
      ? {
          column: userSortModel[0].field,
          direction: userSortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    if (curPartnerName !== newPartnerName) {
      userService.setPartnerName(newPartnerName);
      fetchUserAccounts();
    }
  }, [canManageMolinaUsers]);

  useEffect(() => {
    // userService.setPartnerName(userType);
    dispatch(fetchLookupItemsAsync('userCategoryType'));
  }, [dispatch /*, userType */]);

  useEffect(() => {
    const sort = userSortModel[0]
      ? {
          column: userSortModel[0].field,
          direction: userSortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    fetchUserAccounts();
  }, [searchText, page, pageSize, userSortModel]);

  useEffect(() => {
    const sort = onboardingSortModel[0]
      ? {
          column: onboardingSortModel[0].field,
          direction: onboardingSortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    dispatch(
      fetchOnboardingUserAccountsAsync(
        onboardingPageSize,
        onboardingPage,
        searchText,
        sort,
        false,
      ),
    );
  }, [
    searchText,
    onboardingPage,
    onboardingPageSize,
    onboardingSortModel /*, userType */,
  ]);

  useEffect(() => {
    if (!savingUser && savedUser) {
      handleCloseDialog();
    }
  }, [savingUser, savedUser]);

  const handleEdit = (accountId?: number) => {
    if (accountId) {
      dispatch(fetchUserInfoAsync(accountId));
    } else {
      dispatch(setUserInfo(undefined));
    }
    setUserDialogOpen(true);
  };

  const fetchAllUserAccounts = useCallback(async () => {
    const result = await firstValueFrom(
      userService.findUsers(10000, 1, '', userSortModelMemo, false),
    );
    return (result.items ?? []).map((user) =>
      mapUserToExport(user, userCategoryTypes),
    );
  }, [userSortModelMemo, userCategoryTypes]);

  const fetchAllOnboardingUserAccounts = useCallback(async () => {
    const result = await firstValueFrom(
      userService.findUsers(10000, 1, '', onboardingSortModelMemo, false, true),
    );
    return (result.items ?? []).map((user) =>
      mapUserToExport(user, userCategoryTypes),
    );
  }, [onboardingSortModelMemo, userCategoryTypes]);

  const handleDelete = (accountId: number, userCategoryTypeID: number) => {
    const userCategoryTypeBC = userCategoryTypes.find(
      (type) => type.userCategoryTypeID === userCategoryTypeID,
    )?.userCategoryTypeBC;
    showConfirmationDialog(
      ConfirmationDialogType.Delete,
      t('users.confirmDeleteTitle'),
      t('users.confirmDeleteMessage'),
      () =>
        dispatch(
          deleteUserAsync(accountId, userCategoryTypeBC as UserCategoryTypeBC),
        ),
    );
  };

  const handleCloseDialog = () => {
    setUserDialogOpen(false);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
  };

  const handleOnboardingPageChange = (p: number) => {
    setOnboardingPage(p);
  };

  const handleOnboardingPageSizeChange = (size: number) => {
    setOnboardingPageSize(size);
  };

  const onSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handlePasswordReset = (id: number, email: string) => {
    showConfirmationDialog(
      ConfirmationDialogType.ResetPassword,
      t('users.confirmPasswordResetEmailTitle'),
      t('users.confirmPasswordResetEmailMessage'),
      () => dispatch(sendPasswordResetAsync(id, email)),
    );
  };

  const handleActivationEmail = (id: number, email: string) => {
    showConfirmationDialog(
      ConfirmationDialogType.ActivationEmail,
      t('users.confirmResendActivationEmailTitle'),
      t('users.confirmResendActivationEmailMessage'),
      () => dispatch(sendAccountActivationAsync(id, email)),
    );
  };

  // Separate sort handlers
  const handleUserSortModelChange = (model: GridSortModel) => {
    setUserSortModelLocal(model);
    setPage(1);
  };
  const handleOnboardingSortModelChange = (model: GridSortModel) => {
    setOnboardingSortModelLocal(model);
    setOnboardingPage(1);
  };

  useEffect(() => {
    if (deletedUser) {
      fetchUserAccounts();
      fetchOnboardingUserAccounts();
    }
  }, [deletedUser, fetchUserAccounts, fetchOnboardingUserAccounts]);

  if (loadingSelectedUser) {
    return (
      <StyledBox className={classes.loading}>
        <CircularProgress size={100} sx={{ margin: 'auto' }} />{' '}
      </StyledBox>
    );
  }

  return (
    <StyledBox className={classes.container}>
      <CommonToolbarWithSearchBar
        label={t('users.search')}
        placeholder={t('users.searchPlaceholder')}
        searchText={searchText}
        onSearchTextChange={onSearchTextChange}
        button={
          !isSuperReaderUser &&
          (!canManageMolinaUsers ||
            (canManageMolinaUsers && canCreateMolinaUsers))
            ? t('settings.createUser')
            : null
        }
        onCreate={() => handleEdit()}
        exportDataMap={{
          [ExportEntity.DisplayedUsers]: userAccounts.items.map((user) =>
            mapUserToExport(user, userCategoryTypes),
          ),
          [ExportEntity.AllUsers]: fetchAllUserAccounts,
          [ExportEntity.DisplayedPendingUsers]:
            onboardingUserAccounts.items.map((user) =>
              mapUserToExport(user, userCategoryTypes),
            ),
          [ExportEntity.AllPendingUsers]: fetchAllOnboardingUserAccounts,
        }}
        exportFilenamePrefixMap={{
          [ExportEntity.DisplayedUsers]: ExportFilePrefix.DisplayedUsers,
          [ExportEntity.AllUsers]: ExportFilePrefix.AllUsers,
          [ExportEntity.DisplayedPendingUsers]:
            ExportFilePrefix.DisplayedPendingUsers,
          [ExportEntity.AllPendingUsers]: ExportFilePrefix.AllPendingUsers,
        }}
      />
      <UserDataTable
        users={userAccounts}
        loading={loadingUserAccounts}
        readOnly={false}
        pageSize={pageSize}
        onboarding={false}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onPasswordReset={handlePasswordReset}
        onSortModelChange={handleUserSortModelChange}
        sortModel={userSortModel}
      />
      <UserDataTable
        users={onboardingUserAccounts}
        loading={loadingUserAccounts}
        readOnly={false}
        pageSize={onboardingPageSize}
        onboarding={true}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={handleOnboardingPageChange}
        onPageSizeChange={handleOnboardingPageSizeChange}
        onSendActivationEmail={handleActivationEmail}
        onSortModelChange={handleOnboardingSortModelChange}
        sortModel={onboardingSortModel}
      />

      <UserDialog
        open={userDialogOpen}
        user={user}
        userCategoryTypes={userCategoryTypes}
        close={() => setUserDialogOpen(false)}
      />
      <ConfirmationDialog
        title={confirmationDialogState?.title || ''}
        message={confirmationDialogState?.message || ''}
        open={Boolean(confirmationDialogState)}
        positiveAction={() => {
          confirmationDialogState?.onConfirm?.();
          closeConfirmationDialog();
        }}
        negativeAction={closeConfirmationDialog}
      />
    </StyledBox>
  );
}
